/**
 * CUS-2 — per-ticket custody handoff history panel.
 *
 * Presentational: renders the actor / role / reason / owner-transition
 * trail that `ticket_custody_events` records but no surface previously
 * showed. Takes already-projected entries (from useTicketCustodyHistory)
 * so it stays trivially testable.
 */
import { format, parseISO } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import { custodyActorDisplay, type CustodyHistoryEntry } from '@/lib/tickets/lifecycle/custodyHistory';

interface Props {
  entries: CustodyHistoryEntry[];
  isLoading?: boolean;
  /** actor user id → display name. Missing names fall back to the role. */
  names?: Readonly<Record<string, string>>;
}

/** "Joseph (Technician)" when the name is known; else the role; else "System". */
function formatActor(entry: CustodyHistoryEntry, names?: Readonly<Record<string, string>>): string {
  const who = custodyActorDisplay(entry.actorId, entry.actorRole, names);
  return who.role ? `${who.primary} (${who.role})` : who.primary;
}

function formatWhen(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  return format(parseISO(iso), 'MMM d, h:mm a');
}

export function CustodyHistoryPanel({ entries, isLoading, names }: Props) {
  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground py-2" data-testid="custody-history-loading">
        Loading custody history…
      </div>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <div className="text-xs text-muted-foreground py-2" data-testid="custody-history-empty">
        No custody handoffs recorded yet.
      </div>
    );
  }
  return (
    <ol className="space-y-2" data-testid="custody-history-list">
      {entries.map((e, i) => (
        <li
          key={`${e.createdAt}-${i}`}
          data-testid="custody-history-entry"
          className="text-xs border-l-2 border-border pl-3"
        >
          <div className="flex items-center gap-1 font-medium">
            <span>{e.label}</span>
            {(e.fromOwner || e.toOwner) && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <span>{e.fromOwner ?? '—'}</span>
                <ArrowRight className="h-3 w-3" />
                <span>{e.toOwner ?? '—'}</span>
              </span>
            )}
          </div>
          <div className="text-muted-foreground">
            <span data-testid="custody-history-actor">by {formatActor(e, names)}</span>
            {' · '}
            <span>{formatWhen(e.createdAt)}</span>
          </div>
          {e.reason && (
            <div className="text-muted-foreground" data-testid="custody-history-reason">
              {e.reason}
            </div>
          )}
          {e.notes && (
            <div className="text-foreground/80 italic whitespace-pre-wrap break-words" data-testid="custody-history-notes">
              “{e.notes}”
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
